import {Observable} from 'rxjs';
import i18next from 'i18next';
import React from 'react';

import {combineEpics} from 'web-app/util/redux-observable';
import * as FeedItemObservables from 'web-app/react/entities/feed/items/epics';
import * as FeedItemActions from 'web-app/react/entities/feed/items/actions';
import * as DraftEntityActions from 'web-app/react/entities/drafts/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {showDeleteDialogObservable} from 'web-app/react/dialog';
import * as actionTypes from 'web-app/react/constants/action-types';
import * as DraftActions from 'web-app/react/feed/drafts/actions';
import * as FeedApi from 'web-app/react/entities/feed/posts/api';
import Markdown from 'web-app/react/components/markdown/markdown';

import * as FeedSwitcherConstants from './feed-switcher/constants';
import * as Actions from './actions';

const fetchObservable = (selectedInstitutionId, cursor, heightTarget, olderThan) => {
    switch (selectedInstitutionId) {
        case FeedSwitcherConstants.MY_FEED_ID:
            return FeedItemObservables.fetchObservable(cursor, null, heightTarget, olderThan);
        case FeedSwitcherConstants.MY_BOOKMARKS_ID:
            return FeedItemObservables.fetchBookmarkObservable(cursor, olderThan);
        default:
            return FeedItemObservables.fetchObservable(cursor, selectedInstitutionId, heightTarget, olderThan);
    }
};

const fetch = (action$, state$) =>
    action$.ofType(Actions.fetch.type).mergeMap(({payload}) => {
        const {selectedInstitutionId} = state$.value.feed.switcher;

        return fetchObservable(selectedInstitutionId, payload.cursor, payload.heightTarget, payload.olderThan)
            .flatMap(innerAction => {
                if (innerAction.type === FeedItemActions.fetchSuccess.type) {
                    return [
                        innerAction,
                        Actions.fetchSuccess.action({
                            reload: payload.reload,
                            feedItemIds: innerAction.payload.feedItems.map(item => item.feedItemId),
                        }),
                    ];
                } else {
                    return Observable.of(innerAction);
                }
            })
            .catch(e => Observable.of(Actions.fetchFailed.action(e)))
            .do(() => {
                if (payload.resolve) {
                    payload.resolve();
                }
            });
    });

const deleteSingleItem = action$ =>
    action$.ofType(Actions.deleteSingle.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({
            title: i18next.t('feed.deletePostHeader'),
            description: React.createElement(Markdown, {source: i18next.t('feed.deletePost')}),
            okText: i18next.t('feed.deletePostConfirm'),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return FeedItemObservables.deleteItemObservable(
                    payload.originatorId,
                    payload.feedItemId,
                    payload.observationId,
                )
                    .flatMap(successAction => [
                        successAction,
                        GlobalEventActions.updateSuccess.action(i18next.t('feed.deletePostSuccess')),
                        Actions.deleteSingleSuccess.action(
                            successAction.payload.originatorId,
                            successAction.payload.feedItemId,
                            successAction.payload.observationId,
                        ),
                    ])
                    .do(() => {
                        if (payload.successUrl) {
                            window.location.hash = payload.successUrl;
                        }
                    })
                    .catch(e => Observable.of(Actions.deleteSingleFailed.action(e)));
            }

            return Observable.of(Actions.deleteSingleAborted.action(payload.originatorId));
        }),
    );

const update = (action$, state$) =>
    action$
        .ofType(DraftEntityActions.sendSuccess.type)
        .filter(() => state$.value.feed.showingFeed)
        .map(() => Actions.fetch.action({reload: true}));

const updateAfterPhotoSwipeClose = action$ =>
    action$
        .ofType(actionTypes.CLOSE_IMAGE_GALLERY_SUCCESS)
        .filter(({payload}) => payload.context && payload.context.feedItemId)
        .mergeMap(({payload}) =>
            FeedItemObservables.fetchSingleObservable(payload.context.feedItemId).catch(e =>
                Observable.of(Actions.updateAfterPhotoSwipeCloseFailed.action(e)),
            ),
        );

const updateFeedItem = action$ =>
    action$
        .ofType(Actions.fetchFeedItem.type)
        .mergeMap(({payload}) =>
            FeedItemObservables.fetchSingleObservable(payload.feedItemId).catch(e =>
                Observable.of(Actions.fetchFeedItemFailed.action(e)),
            ),
        );

const focusMode = action$ => {
    const focusModeActions = Observable.merge(
        action$.ofType(Actions.focusMode.type).filter(({payload}) => !payload.isFocused),
        action$
            .ofType(Actions.focusMode.type)
            .filter(({payload}) => payload.isFocused)
            .debounceTime(100),
    )
        .map(({payload}) => payload.isFocused)
        .debounceTime(100);

    const enableActions = Observable.merge(action$.ofType(DraftActions.selectDraftSuccess.type)).map(() => true);

    const disableActions = Observable.merge(
        action$.ofType(DraftEntityActions.sendSuccess.type),
        action$.ofType(Actions.showingFeed.type),
    ).map(() => false);

    return Observable.merge(focusModeActions, enableActions, disableActions).map(isFocused =>
        Actions.focusModeSuccess.action(isFocused),
    );
};

const blockSender = action$ =>
    action$.ofType(Actions.blockSender.type).mergeMap(({payload}) =>
        FeedApi.blockSender(payload.loginId)
            .flatMap(() => [
                Actions.blockSenderSuccess.action(),
                GlobalEventActions.updateSuccess.action(
                    payload.canUnsubscribeFromOrgSender
                        ? i18next.t('feed.unsubscribedFromNewsletter')
                        : i18next.t('feed.senderBlocked'),
                ),
            ])
            .catch(e => Observable.of(Actions.blockSenderFailed.action(e))),
    );

export default combineEpics(
    focusMode,
    updateFeedItem,
    fetch,
    deleteSingleItem,
    updateAfterPhotoSwipeClose,
    update,
    blockSender,
);
